import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { useState } from "react";
import { SiteSettingsProvider, useSiteSettings } from "@/context/SiteSettingsContext";
import { MaintenanceScreen, MAINT_KEY } from "@/components/MaintenanceScreen";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { CartDrawer } from "@/components/layout/CartDrawer";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";

import Home from "@/pages/Home";
import Collection from "@/pages/Collection";
import ProductDetail from "@/pages/ProductDetail";
import CartPage from "@/pages/CartPage";
import Checkout from "@/pages/Checkout";
import Wishlist from "@/pages/Wishlist";
import Account from "@/pages/Account";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import AuthCallback from "@/pages/AuthCallback";
import Admin from "@/pages/Admin";
import Unsubscribe from "@/pages/Unsubscribe";
import LegalPage from "@/pages/LegalPage";

const Layout = ({ children }) => (
  <div className="App flex flex-col min-h-screen">
    <Header />
    <main className="flex-1 pb-16 md:pb-0">{children}</main>
    <Footer />
    <CartDrawer />
    <MobileBottomNav />
  </div>
);

function AppRouter() {
  const location = useLocation();
  const { maintenance, loaded, logo } = useSiteSettings();
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem(MAINT_KEY) === "1");
  // Handle Emergent OAuth callback before any routing/auth check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isAdminRoute = location.pathname.startsWith("/admin")
    || (location.pathname === "/account" && location.search.includes("next=admin"));
  if (!isAdminRoute && !unlocked && !user?.is_admin) {
    if (!loaded) return <div className="fixed inset-0 bg-[#F5EEDC]" />;
    if (maintenance) return <MaintenanceScreen logo={logo} onUnlock={() => setUnlocked(true)} />;
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/collection/:category" element={<Collection />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/account" element={<Account />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WishlistProvider>
          <CartProvider>
            <SiteSettingsProvider>
              <CurrencyProvider>
                <AppRouter />
                <Toaster position="bottom-right" />
              </CurrencyProvider>
            </SiteSettingsProvider>
          </CartProvider>
        </WishlistProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
